import { useContext, useMemo } from 'react';
import SessionContext from 'src/contexts/SessionContext';
import { useTaskById } from 'src/hooks';
import { ListBullets } from '@phosphor-icons/react';
import { SVG_SIZE_M } from 'src/constants';
import {
  ChitChatCard,
  GTMEvent,
  Message,
  MessageType,
  ResearchTaskCreationCard,
  TaskState,
} from 'src/types';
import { sendGTMEvent } from 'src/utils';
import styles from './TaskDetailsButton.module.scss';
import { Button } from 'src/v2/commonComponents/Button';

interface TaskDetailsButtonProps {
  messageId: string;
  taskId?: string;
  taskState?: TaskState;
  messageType?: MessageType;
  payload: Message['payload'];
  content: Message['content'];
}

export const TaskDetailsButton = ({
  messageId,
  messageType,
  taskId,
  taskState,
  payload,
  content,
}: TaskDetailsButtonProps) => {
  const { setCurrentTaskId } = useTaskById();
  const { onToggleVisibleTaskViewModal } = useContext(SessionContext);

  const isContentStartedToCome = useMemo(() => {
    if (messageType === MessageType.RESEARCH_TASK_CREATION_CARD) {
      const data = (payload as ResearchTaskCreationCard)?.research_card?.data;
      return (
        Boolean(data?.search_result?.content?.content) ||
        Boolean(data?.search_result?.summary) ||
        Boolean(data?.deep_research_result?.content?.content) ||
        Boolean(data?.generative_ai_result?.summary)
      );
    }
    if (messageType === MessageType.CHAT_CARD) {
      const data = (payload as ChitChatCard).data;
      return (
        Boolean(data?.ninja_model_output) ||
        Boolean(
          Object.values(data?.referenced_model_summary?.references || {})[0]
            ?.content,
        )
      );
    }
    return false;
  }, [messageType, payload]);

  const isAnimated = useMemo(() => {
    return (
      Boolean(messageId) &&
      !Boolean(content) &&
      (messageType === MessageType.RESEARCH_TASK_CREATION_CARD ||
        messageType === MessageType.CHAT_CARD) &&
      taskState === TaskState.IN_PROGRESS &&
      !isContentStartedToCome
    );
  }, [content, messageId, taskState, messageType, isContentStartedToCome]);

  const handleClick = () => {
    if (!taskId) {
      return;
    }
    setCurrentTaskId(taskId);
    onToggleVisibleTaskViewModal(true);
    sendGTMEvent(GTMEvent.TASK_VIEW_OPENING, {
      taskId,
      task_state: taskState,
    });
  };

  return (
    <div className={styles.buttonWrapper}>
      <Button
        disabled={!taskId}
        onClick={handleClick}
        data-e2e="task-properties-button"
        className={styles.button}
      >
        <ListBullets size={SVG_SIZE_M} />
      </Button>
      {isAnimated && <div className={styles.buttonShadow} />}
    </div>
  );
};
